import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import api from "../../api";

const Gallerie = () => {
  const [images, setImages] = useState([]);
  const [allCategories, setAllCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState("");
  const [modalImage, setModalImage] = useState(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  const pageSize = 12;

  const getImageUrl = (path) => {
    if (!path) return "";
    if (path.startsWith("http://") || path.startsWith("https://")) return path;

    return path.startsWith("/")
      ? `${process.env.REACT_APP_IMG_URL}${path}`
      : `${process.env.REACT_APP_IMG_URL}/${path}`;
  };

  const fetchImages = async (
    pageToLoad = 1,
    reset = false,
    category = selectedCategory
  ) => {
    try {
      pageToLoad === 1 ? setLoading(true) : setLoadingMore(true);
      setError("");

      const response = await api.get("/gallery", {
        params: {
          page: pageToLoad,
          pageSize,
          ...(category ? { category } : {}),
        },
      });

      const newImages = Array.isArray(response.data) ? response.data : [];

      setImages((prevImages) =>
        reset ? newImages : [...prevImages, ...newImages]
      );

      setPage(pageToLoad);
      setHasMore(newImages.length === pageSize);

      if (!category) {
        setAllCategories((prevCategories) => [
          ...new Set([
            ...prevCategories,
            ...newImages.map((image) => image.category).filter(Boolean),
          ]),
        ]);
      }
    } catch (err) {
      console.error(err);
      setError("Erreur lors du chargement des images.");
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    fetchImages(1, true, null);
  }, []);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        closeModal();
      }
    };

    if (modalImage) {
      window.addEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "hidden";
    }

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "auto";
    };
  }, [modalImage]);

  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
    setImages([]);
    setPage(1);
    setHasMore(true);
    fetchImages(1, true, category);
  };

  const handleLoadMore = () => {
    if (!hasMore || loadingMore) return;
    fetchImages(page + 1, false, selectedCategory);
  };

  const openModal = (image) => {
    setModalImage(image);
  };

  const closeModal = () => {
    setModalImage(null);
  };

  const pageTitle = selectedCategory
    ? `Galerie photo - ${selectedCategory} - LMFP Photography`
    : "Galerie photo - LMFP Photography";

  const pageDescription = selectedCategory
    ? `Découvrez la galerie photo ${selectedCategory} de LMFP Photography.`
    : "Explorez la galerie photo de LMFP Photography : portraits, mariages, nature et événements capturés avec émotion.";

  const pageImage =
    images.length > 0 && (images[0]?.thumbnail || images[0]?.fullImage)
      ? getImageUrl(images[0].thumbnail || images[0].fullImage)
      : null;

  const pageUrl = selectedCategory
    ? `${window.location.origin}/galerie?category=${selectedCategory}`
    : `${window.location.origin}/galerie`;

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-900 text-white">
        Chargement...
      </div>
    );
  }

  if (error && images.length === 0) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-gray-900 text-white">
        <p className="mb-4">{error}</p>

        <button
          onClick={() => fetchImages(1, true, selectedCategory)}
          className="rounded bg-orange-500 px-4 py-2 text-white transition hover:bg-orange-700"
        >
          Réessayer
        </button>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://www.lmfp-photography.com/galerie" />

        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:site_name" content="LMFP Photography" />
        <meta property="og:locale" content="fr_FR" />
        {pageImage && <meta property="og:image" content={pageImage} />}

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        {pageImage && <meta name="twitter:image" content={pageImage} />}
      </Helmet>

      <div className="min-h-screen bg-gray-900">
        <div className="container mx-auto p-4">
          <h1 className="mb-6 text-center text-3xl font-bold text-white">
            Galerie
          </h1>

          <div className="mb-8 flex flex-wrap justify-center gap-3">
            <button
              onClick={() => handleCategoryChange(null)}
              className={`rounded-full border px-4 py-2 transition ${
                selectedCategory === null
                  ? "border-orange-500 bg-orange-500 text-white"
                  : "border-gray-700 bg-gray-800 text-gray-300 hover:bg-gray-700"
              }`}
            >
              Toutes
            </button>

            {allCategories.map((category) => (
              <button
                key={category}
                onClick={() => handleCategoryChange(category)}
                className={`rounded-full border px-4 py-2 transition ${
                  selectedCategory === category
                    ? "border-orange-500 bg-orange-500 text-white"
                    : "border-gray-700 bg-gray-800 text-gray-300 hover:bg-gray-700"
                }`}
              >
                {category}
              </button>
            ))}
          </div>

          <div className="mb-6 text-center text-gray-400">
            {images.length} image(s) affichée(s)
          </div>

          {images.length > 0 ? (
            <>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {images.map((image, index) => {
                  const thumbnailPath = image.thumbnail || image.fullImage;
                  const fullImagePath = image.fullImage || image.thumbnail;

                  const heightClass =
                    index % 5 === 0
                      ? "h-80"
                      : index % 5 === 1
                      ? "h-56"
                      : index % 5 === 2
                      ? "h-72"
                      : index % 5 === 3
                      ? "h-64"
                      : "h-60";

                  return (
                    <button
                      type="button"
                      key={image.id || `${thumbnailPath}-${index}`}
                      onClick={() =>
                        openModal({
                          src: getImageUrl(fullImagePath),
                          alt: `Galerie LMFP Photography ${index + 1}`,
                        })
                      }
                      className={`group relative overflow-hidden rounded-xl bg-neutral-900 shadow-md transition duration-300 hover:shadow-xl ${heightClass}`}
                    >
                      {thumbnailPath ? (
                        <img
                          src={getImageUrl(thumbnailPath)}
                          alt={`Galerie LMFP Photography ${index + 1}`}
                          className="h-full w-full object-cover transition duration-300 group-hover:scale-[1.02]"
                          loading="lazy"
                          decoding="async"
                        />
                      ) : (
                        <div className="flex h-full w-full items-center justify-center text-gray-500">
                          Image indisponible
                        </div>
                      )}

                      <div className="pointer-events-none absolute inset-0 flex items-end bg-black/25 p-3 opacity-0 transition duration-300 group-hover:opacity-100">
                        {image.category && (
                          <span className="mt-1 block text-xs text-gray-200">
                            {image.category}
                          </span>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>

              {hasMore && (
                <div className="mt-10 flex justify-center">
                  <button
                    onClick={handleLoadMore}
                    disabled={loadingMore}
                    className="inline-flex items-center rounded-full bg-orange-500 px-6 py-3 text-sm font-medium text-white transition hover:bg-orange-600 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {loadingMore ? "Chargement..." : "Charger plus"}
                  </button>
                </div>
              )}

              {error && (
                <div className="mt-6 text-center text-red-400">{error}</div>
              )}
            </>
          ) : (
            <div className="py-10 text-center text-white">
              Aucune image disponible pour cette catégorie.
            </div>
          )}

          {modalImage && (
            <div
              onClick={closeModal}
              className="fixed inset-0 z-[200] flex items-center justify-center bg-black/90 p-4"
            >
              <div
                onClick={(e) => e.stopPropagation()}
                className="relative flex animate-fadeIn flex-col items-center"
              >
                <button
                  onClick={closeModal}
                  className="absolute -right-4 -top-4 z-10 flex h-10 w-10 items-center justify-center rounded-full bg-black/60 text-2xl font-bold text-white transition hover:bg-black/80"
                  aria-label="Fermer"
                >
                  &times;
                </button>

                <img
                  src={modalImage.src}
                  alt={modalImage.alt || "Grande vue"}
                  className="h-auto max-h-[85vh] w-auto max-w-[95vw] rounded-lg shadow-2xl"
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default Gallerie;